import { useEffect, useRef, useState } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import useCategories from "../../../Hooks/useCategories";
import * as Utilities from "../../../Utilities/utils";
import { UseUserContext } from "../../Contexts/UserContext";
import { UseVideoContext } from "../../Contexts/VideoContext";
import PageSelector from "../../Generic/PageSelector";
import VideoList from "../../Lists/VideoList";
import VideoListFilter from "./VideoListFilter";
import Ad from "../../Generic/Ad";
import ArmanetAd from "../../Generic/ArmanetAd";
import useIsMobile from "../../../Hooks/useIsMobile";

function FullVideoList(props) {
  const [filter, SetFilter] = useState(null);
  const [listComplete, SetListComplete] = useState(false);
  const [resetToPage1, SetResetToPage1] = useState(false);
  const [page, SetPage] = useState(0);
  const [totalPages, SetTotalPages] = useState(0);

  const { loadedCategories } = UseVideoContext();
  const { SetReturnToPath } = UseUserContext();
  const { categories, sort, days } = useParams();
  const [searchParams, SetSearchParams] = useSearchParams();
  const categoryIds = useCategories(categories);
  const { isMobile } = useIsMobile();

  const { topVideo, loadingTopVideo } = useOutletContext();

  function ChangeTotalPages(total) {
    SetTotalPages(total);
  }

  function ScrollToTop() {
    if (listRef.current != null) {
      listRef.current.scrollTop = 0;
    }
  }

  function GetStartDate(days) {
    let date = new Date();
    let currentMonth = date.getMonth();
    switch (days) {
      case "this-week":
        date.setDate(date.getDate() - 7);
        break;
      case "this-month":
        date.setMonth(currentMonth - 1);
        if (currentMonth === date.getMonth()) {
          date.setDate(0);
        }
        break;
      case "this-year":
        date.setFullYear(date.getFullYear() - 1);
        if (currentMonth !== date.getMonth()) {
          date.setMonth(currentMonth + 1);
          date.setDate(0);
        }
        break;
      default:
        return null;
    }
    return date.toISOString();
  }

  function ProcessUrlChange() {
    ScrollToTop();
    const newFilter = {};
    newFilter.sortBy = sort ?? "date";
    newFilter.dateRange = days ?? "all-time";
    newFilter.startDate = GetStartDate(newFilter.dateRange);
    newFilter.search = searchParams.get("search");
    newFilter.amount = 40;
    if (
      totalPages !== 0 &&
      (!Utilities.arraysEqual(newFilter.categories, filter?.categories) ||
        newFilter.sortBy !== filter?.sortBy ||
        newFilter.dateRange !== filter?.dateRange ||
        newFilter.search !== filter?.search)
    ) {
      SetResetToPage1(true);
    }
    SetFilter(newFilter);
    SetReturnToPath(
      window.location.pathname + window.location.search,
      "Video List",
    );
  }

  const listRef = useRef();

  function ChangeListCompleted(value) {
    SetListComplete(value);
  }

  function HandlePageChange(pageNumber) {
    switch (pageNumber) {
      case "first":
        SetPage(1);
        break;
      case "previous":
        if (page > 1) SetPage(page - 1);
        break;
      case "next":
        if (!listComplete) SetPage(page + 1);
        break;
      case "last":
        SetPage(totalPages);
        break;
      default:
        SetPage(pageNumber);
        break;
    }
  }

  useEffect(() => {
    let currentParams = {};

    searchParams.forEach((value, key) => {
      currentParams[key] = value;
    });

    currentParams.page = parseInt(currentParams.page ?? "1");

    if (resetToPage1) {
      if (page !== 0) {
        SetSearchParams({ ...currentParams, page: 0 });
        SetPage(0);
        return;
      }
      SetPage(1);
      SetResetToPage1(false);
    }

    if (currentParams.page !== 0 && isNaN(currentParams.page)) {
      currentParams.page = 1;
    }

    if (currentParams.page < 1) {
      SetSearchParams({ ...currentParams, page: 1 });
      SetPage(1);
      SetListComplete(false);
      return;
    }

    if (currentParams.page !== page && page > 0) {
      SetSearchParams({ ...currentParams, page: page });
      return;
    }

    SetPage(currentParams.page);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, resetToPage1]);

  useEffect(() => {
    if (loadedCategories?.length > 0) {
      ProcessUrlChange();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    window.location.pathname,
    window.location.searchParams,
    loadedCategories,
    searchParams,
  ]);

  return (
    <div className="fullVideoList" ref={listRef}>
      {isMobile && <ArmanetAd type="leaderboard" />}
      {filter != null ? (
        <>
          <VideoListFilter
            sortBy={filter.sortBy}
            categories={filter.categories}
            dateRange={filter.dateRange}
            page={page}
            HandlePageChange={HandlePageChange}
            noMorePages={listComplete}
          />
          <PageSelector
            currentPage={page}
            HandlePageChange={HandlePageChange}
            pages={[page]}
            totalPages={totalPages}
          />
          <div className="searchingForLabel">
            {filter.search != null
              ? "Search - " + searchParams.get("search")
              : null}
          </div>
          <VideoList
            hideHeader="true"
            listId="fullVideoList"
            sortBy={filter.sortBy}
            startDate={filter.startDate}
            endDate={filter.endDate}
            search={filter.search}
            categories={categoryIds}
            channel={filter.channel}
            amount={filter.amount}
            loadsCompleted={page}
            ChangeListCompleted={ChangeListCompleted}
            listComplete={listComplete}
            isPaginated="true"
            adAtStart={false}
            numberOfAdsInMiddle={3}
            adAtEnd={true}
            includeTotalPages={true}
            ChangeTotalPages={ChangeTotalPages}
            topVideo={topVideo}
            loadingTopVideo={loadingTopVideo}
            showArmanetRectangle={true}
            armanetRectanglePosition={isMobile ? 0 : 3}
          />
          <PageSelector
            currentPage={page}
            HandlePageChange={HandlePageChange}
            pages={[page]}
            totalPages={totalPages}
          />

          {isMobile && (
            <div style={{ marginTop: 16 }}>
              <Ad type="banner" />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default FullVideoList;
